import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import AuthShell from '@/components/auth/AuthShell';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [checking, setChecking] = useState(true);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let active = true;
    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!active) return;
        setReady(Boolean(data.session));
        setChecking(false);
      })
      .catch(() => {
        if (active) setChecking(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (password.length < 6) {
      setError('비밀번호는 6자 이상이어야 해요.');
      return;
    }
    if (password !== confirm) {
      setError('비밀번호가 서로 일치하지 않아요.');
      return;
    }
    setSubmitting(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    if (err) {
      setSubmitting(false);
      setError(err.message);
      return;
    }
    await supabase.auth.signOut();
    setSubmitting(false);
    setDone(true);
  };

  if (checking) {
    return (
      <AuthShell title="비밀번호 재설정" subtitle="잠시만 기다려 주세요.">
        <div className="flex items-start gap-2.5 rounded-md border border-secondary-200 bg-secondary-50 px-3.5 py-3 text-xs leading-relaxed text-foreground-700">
          <i className="ri-loader-4-line mt-0.5 animate-spin text-base text-secondary-700"></i>
          <span>재설정 세션을 확인하는 중이에요...</span>
        </div>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell
        title="비밀번호가 변경되었습니다"
        subtitle="새 비밀번호로 로그인해 주세요."
        footer={
          <Link to="/login" className="font-bold text-primary-700 hover:underline">
            로그인 화면으로
          </Link>
        }
      >
        <button
          type="button"
          onClick={() => navigate('/login', { replace: true })}
          className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-bold text-background-50 transition hover:bg-primary-600"
        >
          <i className="ri-login-box-line text-base"></i>
          로그인하러 가기
        </button>
      </AuthShell>
    );
  }

  if (!ready) {
    return (
      <AuthShell
        title="재설정 링크가 유효하지 않아요"
        subtitle="비밀번호 재설정 메일을 새로 요청해 주세요."
        footer={
          <Link to="/login" className="font-bold text-primary-700 hover:underline">
            로그인 화면으로
          </Link>
        }
      >
        <div className="flex items-start gap-2.5 rounded-md border border-accent-200 bg-accent-50 px-3.5 py-3 text-xs leading-relaxed text-accent-800">
          <i className="ri-error-warning-line mt-0.5 text-base"></i>
          <span>재설정 세션을 찾을 수 없어요. 링크가 만료되었거나 이미 사용되었을 수 있어요.</span>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="새 비밀번호 설정"
      subtitle="새로 사용할 비밀번호를 입력해 주세요."
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <div>
          <label htmlFor="reset-password" className="mb-1.5 block text-xs font-semibold text-foreground-600">
            새 비밀번호 (6자 이상)
          </label>
          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 transition focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100">
            <i className="ri-lock-line text-base text-foreground-400"></i>
            <input
              id="reset-password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="새 비밀번호"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded text-foreground-400 transition hover:bg-background-200 hover:text-foreground-700"
              aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
            >
              <i className={showPassword ? 'ri-eye-off-line text-base' : 'ri-eye-line text-base'}></i>
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="reset-confirm" className="mb-1.5 block text-xs font-semibold text-foreground-600">
            새 비밀번호 확인
          </label>
          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 transition focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100">
            <i className="ri-lock-2-line text-base text-foreground-400"></i>
            <input
              id="reset-confirm"
              type={showPassword ? 'text' : 'password'}
              name="confirm"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="새 비밀번호 다시 입력"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none"
            />
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-xs leading-relaxed text-accent-800">
            <i className="ri-error-warning-line mt-0.5 text-sm"></i>
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="mt-1 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-bold text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? (
            <>
              <i className="ri-loader-4-line animate-spin text-base"></i>
              변경 중...
            </>
          ) : (
            <>
              <i className="ri-check-line text-base"></i>
              비밀번호 변경
            </>
          )}
        </button>
      </form>
    </AuthShell>
  );
}